/**
 * Rehype plugin that tags each GFM task-list checkbox with a stable, document-order
 * index (`data-task-index`) and its label text (`data-task-label`) during markdown
 * processing. The MarkdownRenderer reads these at render time to track which checkbox
 * was toggled and what it referred to, without re-parsing the source markdown.
 */

/** HAST property name for `data-task-index` (see property-information). */
export const TASK_INDEX_PROPERTY = 'dataTaskIndex';
/** HAST property name for `data-task-label`. */
export const TASK_LABEL_PROPERTY = 'dataTaskLabel';

type HastProperties = Record<string, unknown> & {
  type?: string | string[];
  className?: string | string[];
};

type HastElement = {
  type: 'element';
  tagName: string;
  properties: HastProperties;
  children: HastNode[];
};

export type HastRoot = {
  type: 'root';
  children: HastNode[];
};

type HastText = { type: 'text'; value: string };

type HastNode = HastElement | HastText;

const isHastElement = (node: HastNode): node is HastElement => node.type === 'element';

const isCheckboxInput = (properties: HastProperties | undefined): boolean => {
  const type = properties?.type;
  return type === 'checkbox' || (Array.isArray(type) && type.includes('checkbox'));
};

const hasTaskListItemClass = (className: HastProperties['className']): boolean => {
  if (typeof className === 'string') {
    return className.split(/\s+/).includes('task-list-item');
  }
  if (Array.isArray(className)) {
    return className.includes('task-list-item');
  }
  return false;
};

// Gather plain text from a task-list-item's children. Skips the checkbox input
// (already captured as the checkbox itself) and nested `ul`/`ol` lists (which own
// their own task items with their own labels).
const collectLabelText = (nodes: HastNode[]): string => {
  let text = '';
  for (const node of nodes) {
    if (node.type === 'text') {
      text += node.value;
      continue;
    }
    if (!isHastElement(node)) {
      continue;
    }
    if (node.tagName === 'input' || node.tagName === 'ul' || node.tagName === 'ol') {
      continue;
    }
    text += collectLabelText(node.children);
  }
  return text;
};

const visit = (node: HastRoot | HastElement, taskListItem: HastElement | null, nextIndex: { value: number }): void => {
  for (const child of node.children) {
    if (!isHastElement(child)) {
      continue;
    }

    // Track the closest enclosing task-list-item so nested task lists each get their
    // own label scoped to their own `<li>`.
    const childTaskListItem =
      child.tagName === 'li' && hasTaskListItemClass(child.properties.className) ? child : taskListItem;

    if (child.tagName === 'input' && isCheckboxInput(child.properties) && childTaskListItem) {
      child.properties[TASK_INDEX_PROPERTY] = nextIndex.value;
      child.properties[TASK_LABEL_PROPERTY] = collectLabelText(childTaskListItem.children).trim();
      nextIndex.value += 1;
    }

    visit(child, childTaskListItem, nextIndex);
  }
};

/**
 * Assigns stable `data-task-index` values and `data-task-label` text to GFM task-list
 * checkboxes in document order. Runs during markdown processing (not React render), so
 * the metadata stays correct under Strict Mode and concurrent rendering.
 */
export const rehypeTaskListIndices = () => (tree: HastRoot) => {
  visit(tree, null, { value: 0 });
};

export const parseTaskIndex = (node?: { properties?: HastProperties }): number => {
  const raw = node?.properties?.[TASK_INDEX_PROPERTY];
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string') {
    const parsed = Number.parseInt(raw, 10);
    return Number.isNaN(parsed) ? -1 : parsed;
  }
  return -1;
};

export const parseTaskLabel = (node?: { properties?: HastProperties }): string => {
  const raw = node?.properties?.[TASK_LABEL_PROPERTY];
  return typeof raw === 'string' ? raw : '';
};
